import { Controller } from "@hotwired/stimulus"
import { requestConfirm } from "../util/confirm_modal"

export default class extends Controller {
  static targets = ["field"]

  initialState = {}
  isNavigatingAway = false
  hasBrowserBackGuard = false

  connect() {
    this.addNavigationListeners()
    this.element.addEventListener("submit", this.handleFormSubmit)

    requestAnimationFrame(() => {
      this.captureInitialState()
      this.addFieldListeners()
    })
  }

  disconnect() {
    this.removeNavigationListeners()
    this.removeFieldListeners()
    this.element.removeEventListener("submit", this.handleFormSubmit)
    this.removeBrowserBackGuard()
  }

  fieldTargetConnected(field) {
    if (!this.initialState) return

    this.attachFieldListener(field)
  }

  fieldTargetDisconnected(field) {
    this.detachFieldListener(field)
  }

  addNavigationListeners() {
    document.addEventListener("click", this.handleLinkClick, true)
    window.addEventListener("popstate", this.handlePopState)
    window.addEventListener("beforeunload", this.handleBeforeUnload)
  }

  removeNavigationListeners() {
    document.removeEventListener("click", this.handleLinkClick, true)
    window.removeEventListener("popstate", this.handlePopState)
    window.removeEventListener("beforeunload", this.handleBeforeUnload)
  }

  captureInitialState() {
    this.initialState = this.getCurrentState()
  }

  addFieldListeners() {
    this.fieldTargets.forEach(field => this.attachFieldListener(field))
  }

  removeFieldListeners() {
    this.fieldTargets.forEach(field => this.detachFieldListener(field))
  }

  attachFieldListener(field) {
    if (field.tomselect) {
      field.tomselect.on("change", this.handleFieldChange)
    }
    field.addEventListener("input", this.handleFieldChange)
    field.addEventListener("change", this.handleFieldChange)
  }

  detachFieldListener(field) {
    if (field.tomselect) {
      field.tomselect.off("change", this.handleFieldChange)
    }
    field.removeEventListener("input", this.handleFieldChange)
    field.removeEventListener("change", this.handleFieldChange)
  }

  handleLinkClick = async (event) => {
    const link = event.target.closest("a[href]")

    if (this.shouldIgnoreLinkClick(link, event)) return
    if (this.isNavigatingAway || !this.hasChanges()) return

    event.preventDefault()
    event.stopPropagation()

    const confirmed = await this.showConfirmModal()

    if (confirmed) {
      this.isNavigatingAway = true
      this.removeBrowserBackGuard()
      window.location.href = link.href
    }
  }

  shouldIgnoreLinkClick(link, event) {
    if (!link) return true
    if (link.getAttribute("href")?.startsWith("#")) return true
    if (event.target.closest(".ts-wrapper")) return true
    if (this.loadsIntoTurboFrame(link)) return true

    return false
  }

  loadsIntoTurboFrame(link) {
    const turboFrame = link.getAttribute("data-turbo-frame")
    return Boolean(turboFrame) && turboFrame !== "_top"
  }

  handlePopState = async () => {
    if (!this.hasBrowserBackGuard || !this.hasChanges() || this.isNavigatingAway) return

    this.hasBrowserBackGuard = false

    const confirmed = await this.showConfirmModal()

    if (confirmed) {
      this.isNavigatingAway = true
      history.back()
    } else {
      this.addBrowserBackGuard()
    }
  }

  handleBeforeUnload = (event) => {
    if (this.isNavigatingAway || !this.hasChanges()) return

    event.preventDefault()
    event.returnValue = ""
  }

  handleFormSubmit = () => {
    this.isNavigatingAway = true
    this.removeBrowserBackGuard()
  }

  handleFieldChange = () => {
    if (this.shouldAddBrowserBackGuard()) {
      this.addBrowserBackGuard()
    }
  }

  shouldAddBrowserBackGuard() {
    return this.hasChanges() && !this.hasBrowserBackGuard
  }

  addBrowserBackGuard() {
    if (this.hasBrowserBackGuard) return

    history.pushState({ unsavedChangesGuard: true }, "", window.location.href)
    this.hasBrowserBackGuard = true
  }

  removeBrowserBackGuard() {
    if (!this.hasBrowserBackGuard) return

    this.hasBrowserBackGuard = false
  }

  showConfirmModal() {
    return requestConfirm({ modal: "unsaved-changes" })
  }

  hasChanges() {
    const currentState = this.getCurrentState()
    const allKeys = new Set([...Object.keys(this.initialState), ...Object.keys(currentState)])

    for (const key of allKeys) {
      const initial = this.initialState[key]
      const current = currentState[key]

      if (!this.valuesEqual(initial, current)) return true
    }
    return false
  }

  getCurrentState() {
    const currentState = {}
    this.fieldTargets.forEach(field => {
      const key = this.getFieldKey(field)
      const value = this.getFieldValue(field)

      if (key in currentState) {
        if (!Array.isArray(currentState[key])) currentState[key] = [ currentState[key] ]
        currentState[key].push(value)
      } else {
        currentState[key] = value
      }
    })
    return currentState
  }

  valuesEqual(initial, current) {
    const isArrayInitial = Array.isArray(initial)
    const isArrayCurrent = Array.isArray(current)

    if (isArrayInitial || isArrayCurrent) {
      if (!isArrayInitial || !isArrayCurrent) return false
      if (initial.length !== current.length) return false
      return initial.every((val, i) => val === current[i])
    }

    return initial === current
  }

  getFieldKey(element) {
    return element.name || element.id
  }

  getFieldValue(element) {
    if (element.tomselect) {
      const values = element.tomselect.getValue()
      return Array.isArray(values) ? values.toSorted() : values
    }
    if (element.tagName === "SELECT" && element.multiple) {
      return Array.from(element.selectedOptions).map(opt => opt.value).toSorted()
    }
    if (element.tagName === "INPUT" || element.tagName === "TEXTAREA") {
      return element.value.trim()
    }
    return element.value
  }
}
